import * as React from "react"
import { cva } from "class-variance-authority"
import { cn } from "@/lib/utils"
import { Popover as PopoverPrimitive } from "radix-ui"

// Every floating surface builds on this: popover, hover card, menus, select,
// combobox, navigation menu viewport and toasts. `list` is for surfaces that
// hold rows (md radius, 4px inset), `panel` for free content (lg radius, 12px
// padding). `motion` is the Radix side-aware enter and exit.
const floatingSurfaceVariants = cva(
  "z-50 border border-border bg-popover text-ui text-popover-foreground shadow-overlay outline-hidden",
  {
    variants: {
      kind: {
        list: "rounded-md p-1",
        panel: "rounded-lg p-3",
      },
      motion: {
        radix:
          "duration-fast ease-standard data-[side=bottom]:slide-in-from-top-1 data-[side=left]:slide-in-from-right-1 data-[side=right]:slide-in-from-left-1 data-[side=top]:slide-in-from-bottom-1 data-[state=closed]:animate-out data-[state=closed]:fade-out-0 data-[state=open]:animate-in data-[state=open]:fade-in-0 motion-reduce:animate-none",
        none: "",
      },
    },
    defaultVariants: {
      kind: "panel",
      motion: "radix",
    },
  },
)

const floatingPanelClassName = floatingSurfaceVariants({ kind: "panel" })

function Popover({
  ...props
}: React.ComponentProps<typeof PopoverPrimitive.Root>) {
  return <PopoverPrimitive.Root data-slot="popover" {...props} />
}

function PopoverTrigger({
  ...props
}: React.ComponentProps<typeof PopoverPrimitive.Trigger>) {
  return <PopoverPrimitive.Trigger data-slot="popover-trigger" {...props} />
}

function PopoverClose({
  ...props
}: React.ComponentProps<typeof PopoverPrimitive.Close>) {
  return <PopoverPrimitive.Close data-slot="popover-close" {...props} />
}

// Popover content is a dialog, so it needs a name. A PopoverTitle inside
// registers itself and becomes the name unless aria-label(ledby) is set.
const PopoverTitleContext = React.createContext<{
  id: string
  register: (id: string) => () => void
} | null>(null)

function PopoverContent({
  className,
  align = "center",
  sideOffset = 4,
  children,
  ...props
}: React.ComponentProps<typeof PopoverPrimitive.Content>) {
  const fallbackId = React.useId()
  const [titleId, setTitleId] = React.useState<string>()
  const register = React.useCallback((id: string) => {
    setTitleId(id)
    return () => setTitleId(undefined)
  }, [])
  const title = React.useMemo(
    () => ({ id: fallbackId, register }),
    [fallbackId, register],
  )

  return (
    <PopoverPrimitive.Portal>
      <PopoverPrimitive.Content
        data-slot="popover-content"
        align={align}
        sideOffset={sideOffset}
        aria-labelledby={props["aria-label"] ? undefined : titleId}
        className={cn(
          floatingPanelClassName,
          "max-h-(--radix-popover-content-available-height) w-72 origin-(--radix-popover-content-transform-origin) overflow-y-auto",
          className,
        )}
        {...props}
      >
        <PopoverTitleContext.Provider value={title}>
          {children}
        </PopoverTitleContext.Provider>
      </PopoverPrimitive.Content>
    </PopoverPrimitive.Portal>
  )
}

function PopoverAnchor({
  ...props
}: React.ComponentProps<typeof PopoverPrimitive.Anchor>) {
  return <PopoverPrimitive.Anchor data-slot="popover-anchor" {...props} />
}

function PopoverHeader({ className, ...props }: React.ComponentProps<"div">) {
  return (
    <div
      data-slot="popover-header"
      className={cn("mb-3 flex flex-col gap-0.5", className)}
      {...props}
    />
  )
}

function PopoverTitle({
  className,
  id,
  ...props
}: React.ComponentProps<"div">) {
  const title = React.useContext(PopoverTitleContext)
  const resolvedId = id ?? title?.id
  React.useLayoutEffect(() => {
    if (title && resolvedId) return title.register(resolvedId)
  }, [title, resolvedId])

  return (
    <div
      data-slot="popover-title"
      id={resolvedId}
      className={cn("text-ui font-semibold text-foreground", className)}
      {...props}
    />
  )
}

function PopoverDescription({
  className,
  ...props
}: React.ComponentProps<"p">) {
  return (
    <p
      data-slot="popover-description"
      className={cn("text-meta text-muted-foreground", className)}
      {...props}
    />
  )
}

function PopoverFooter({ className, ...props }: React.ComponentProps<"div">) {
  return (
    <div
      data-slot="popover-footer"
      className={cn(
        "-mx-3 mt-3 -mb-3 flex items-center justify-end gap-2 border-t border-border px-3 py-2",
        className,
      )}
      {...props}
    />
  )
}

export {
  Popover,
  PopoverTrigger,
  PopoverClose,
  PopoverContent,
  PopoverAnchor,
  PopoverHeader,
  PopoverTitle,
  PopoverDescription,
  PopoverFooter,
  floatingPanelClassName,
  floatingSurfaceVariants,
}
